'use client';

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from './AdminLayout';
import {
  Cog6ToothIcon,
  UserGroupIcon,
  BellIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  BuildingOffice2Icon,

  PhotoIcon,
  EnvelopeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface AdminTool {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  href: string;
}

export default function AdminToolsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user) {
      if (!user.is_system_admin) {
        navigate('/dashboard');
      } else {
        setIsAdmin(true);
      }
    }
  }, [user, navigate]);

  const adminTools: AdminTool[] = [
    {
      id: 'settings',
      name: 'System Settings',
      description: 'Configure global system settings and parameters.',
      icon: <Cog6ToothIcon className="h-8 w-8 text-blue-500" />,
      href: '/admin-tools/settings'
    },
    {
      id: 'users',
      name: 'User Management',
      description: 'Manage users, roles, and permissions.',
      icon: <UserGroupIcon className="h-8 w-8 text-green-500" />,
      href: '/admin-tools/users'
    },
    {
      id: 'tenants',
      name: 'Tenant Management',
      description: 'Manage tenant subscriptions and settings.',
      icon: <BuildingOffice2Icon className="h-8 w-8 text-purple-500" />,
      href: '/admin-tools/tenants'
    },
    {
      id: 'email',
      name: 'Email System',
      description: 'Manage email templates and view delivery logs.',
      icon: <EnvelopeIcon className="h-8 w-8 text-yellow-500" />,
      href: '/admin-tools/email'
    },
    {
      id: 'migration',
      name: 'Content Migration',
      description: 'Migrate articles and assets between tenants and users.',
      icon: <ArrowPathIcon className="h-8 w-8 text-pink-500" />,
      href: '/admin-tools/articles'
    },
    // Development tools - only shown in dev mode
    ...(import.meta.env.DEV ? [{
      id: 'image-generation',
      name: 'AI Image Generator',
      description: 'Test AI image generation with Gemini API. Generated images are saved to Assets.',
      icon: <PhotoIcon className="h-8 w-8 text-orange-500" />,
      href: '/admin-tools/image-generator'
    }] : []),
    /*
    {
      id: 'notifications',
      name: 'Notification Templates',
      description: 'Customize system-wide notification templates.',
      icon: <BellIcon className="h-8 w-8 text-yellow-500" />,
      href: '/admin-tools/notifications'
    },
    {
      id: 'analytics',
      name: 'System Analytics',
      description: 'View performance and usage statistics.',
      icon: <ChartBarIcon className="h-8 w-8 text-red-500" />,
      href: '/admin-tools/analytics'
    },
    {
      id: 'security',
      name: 'Security Settings',
      description: 'Configure security policies and audit logs.',
      icon: <ShieldCheckIcon className="h-8 w-8 text-indigo-500" />,
      href: '/admin-tools/security'
    }
    */
  ];

  if (!isAdmin) {
    return null; // Don't render anything while checking admin status or if not an admin
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <WrenchScrewdriverIcon className="h-8 w-8 text-gray-700 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Admin Tools</h1>
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-600">Central hub for managing system-wide settings, users, and security.</p>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminTools.map((tool) => (
          <Link
            key={tool.id}
            to={tool.href}
            className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full border border-gray-200"
          >
            <div className="p-6 flex-grow">
              <div className="flex items-start mb-4">
                <div className="bg-gray-100 p-3 rounded-lg">
                  {tool.icon}
                </div>
              </div>
              <h2 className="text-lg font-semibold text-gray-800">{tool.name}</h2>
              <p className="text-gray-600 mt-1 text-sm">{tool.description}</p>
            </div>
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 mt-auto">
              <span className="text-blue-600 font-medium text-sm">Go to Tool →</span>
            </div>
          </Link>
        ))}
      </div>
    </AdminLayout>
  );
}
